import React, { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { addClienteEmail } from '../../../../../Features/letterBox/api/letterBox.api';
import { Alert, Button, Input, Label, ListGroup, ListGroupItem, Modal, ModalBody, ModalFooter, ModalHeader, Spinner } from 'reactstrap';
import { get } from '../../../../../hook/request';

type Cliente = { ID: number; ragionesociale?: string; nominativo?: string; codice?: string };

export default function LetterClientModal({ email, onClose }: { email: string; onClose: () => void }) {
    const [search, setSearch] = useState('');
    const [query, setQuery] = useState('');
    const [selected, setSelected] = useState<Cliente | null>(null);
    const queryClient = useQueryClient();
    const save = useMutation({
        mutationFn: addClienteEmail,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['letterBox-list'] });
            queryClient.invalidateQueries({ queryKey: ['letterBox-detail'] });
            toast.success('Cliente associato all’email.');
            onClose();
        },
    });
    useEffect(() => {
        const timer = window.setTimeout(() => setQuery(search.trim()), 300);
        return () => window.clearTimeout(timer);
    }, [search]);
    const clients = useQuery({
        queryKey: ['letter-client-search', query],
        queryFn: async () => {
            const response = await get<Cliente[]>(`/clienti/searchRagioneSociale?temp=${encodeURIComponent(query)}`);
            return Array.isArray(response.data) ? response.data : [];
        },
        enabled: query.length >= 2 && search.trim() === query && !selected,
        retry: false,
    });
    return (
        <Modal isOpen toggle={save.isPending ? undefined : onClose} backdrop={save.isPending ? 'static' : true} keyboard={!save.isPending} size="lg">
            <ModalHeader toggle={save.isPending ? undefined : onClose}>Aggiungi Cliente</ModalHeader>
            <ModalBody>
                {save.isError && <Alert color="danger">{save.error instanceof Error ? save.error.message : 'Errore durante il salvataggio.'}</Alert>}
                <div className="mb-3">
                    <Label for="letter-client-email">Email</Label>
                    <Input id="letter-client-email" value={email} readOnly />
                </div>
                <Label for="letter-client-search">Cliente</Label>
                <Input id="letter-client-search" placeholder="Cerca cliente..." value={search} disabled={save.isPending} onChange={event => { setSearch(event.target.value); setSelected(null); save.reset(); }} autoComplete="off" />
                {search.trim().length < 2 && <small className="text-muted">Inserisci almeno 2 caratteri.</small>}
                {!selected && search.trim() === query && query.length >= 2 && <>
                    {clients.isFetching && <div className="mt-2" role="status"><Spinner size="sm" /> Ricerca clienti...</div>}
                    {clients.isError && <Alert color="danger" className="mt-2">Errore nella ricerca clienti.</Alert>}
                    {clients.data && !clients.isFetching && !clients.isError && clients.data.length === 0 && <p className="text-muted mt-2">Nessun cliente trovato.</p>}
                    {!!clients.data?.length && <ListGroup className="mt-2" style={{ maxHeight: 280, overflowY: 'auto' }}>
                        {clients.data.map(client => <ListGroupItem key={client.ID} tag="button" type="button" action onClick={() => {
                            setSelected(client);
                            setSearch(client.ragionesociale || client.nominativo || `Cliente #${client.ID}`);
                        }}>
                            {client.ragionesociale || client.nominativo || `Cliente #${client.ID}`}
                            {client.codice && <span className="text-muted"> ({client.codice})</span>}
                        </ListGroupItem>)}
                    </ListGroup>}
                </>}
                {selected && <div className="mt-2 text-primary">Cliente selezionato: <strong>{selected.ragionesociale || selected.nominativo || `Cliente #${selected.ID}`}</strong></div>}
            </ModalBody>
            <ModalFooter>
                <Button type="button" color="secondary" disabled={save.isPending} onClick={onClose}>Chiudi</Button>
                <Button type="button" color="primary" disabled={!selected || !email || save.isPending} onClick={() => {
                    if (selected && email && !save.isPending) save.mutate({ email, clienteID: Number(selected.ID) });
                }}>{save.isPending ? 'Salvataggio...' : 'Aggiungi Cliente'}</Button>
            </ModalFooter>
        </Modal>
    );
}
